'use strict';

const _ = require('lodash');
const d3 = require('d3');
const numeral = require('numeral');

const Controller = require('../shared/controller');
const APIController = require('../shared/api-controller');
const utils = require('../shared/utils');

const {Toggle, Toggles, SimpleToggles, BaseToggles, AsteriskToggles} = require('../shared/toggles');

exports.Toggle = Toggle;
exports.BaseToggles = BaseToggles;
exports.SimpleToggles = SimpleToggles;
exports.Toggles = Toggles;
exports.AsteriskToggles = AsteriskToggles;

class BasicTogglesController extends Controller {
	constructor(options) {
		options = Object.assign({
			getTitle: _.property('title'),
			coloured: true, queryKey: '',
			itemKey: 'key', css: 'cm-toggles cm-checkboxes',
			containerID: _.uniqueId('cm-toggles-')
		}, ... arguments);
		super(options);

		if (options.toggles) {
			this._toggles = options.toggles;
		} else {
			this._toggles = options.toggles = new Toggles();
		}

		_.bindAll(this, 'drawItem', 'setItem', 'drawItems', 'itemColourIndex', 'itemStateClass');
		utils.bindHandlers(this, 'itemText', 'itemClass', 'itemOnClick');
	}

	get toggles() {
		return this._toggles;
	}

	itemColourIndex(d, i) {
		if (!d.on) {
			return '0';
		}

		if (this.options.coloured) {
			return (i + 1).toString();
		} else {
			return '1';
		}
	}

	itemStateClass(d, i) {
		if (d.disabled) {
			return 'cm-disabled';
		}
		return d.isOn ? 'cm-on' : 'cm-off';
	}

	itemClass(el, d, i) {
		return utils.cssClasses(
			'cm-item', 'cm-toggle', this.itemStateClass(d, i),
			'zl-text-ci-' + this.itemColourIndex(d, i)
		);
	}

	itemText(el, d, i) {
		let getter = this.options.getTitle;
		return (_.isFunction(getter) && getter(d.value)) || d.key;
	}

	itemOnClick(el, d, i) {
		this.toggles.toggle(d);
		this.emit('cm:update');
		this.update();
		return false;
	}

	setItem(selector) {
		selector.attr('class', this.itemClass).on('click', this.itemOnClick);
		selector.select('.cm-title').text(this.itemText);
	}

	drawItem(selector) {
		selector.append('div').attr('class', 'cm-title');
		return selector;
	}

	data() {
		let sort = this.options.sort || this.options.itemKey;
		if (!_.isFunction(sort)) {
			sort = _.property(sort);
		}
		return _.sortBy(Array.from(this.toggles.items.values()), [function (t) {
			let v = t.value;
			if (_.isObject(v)) {
				return sort(v);
			} else {
				return t.key;
			}
		}]);
	}

	drawItems(selector, options) {
		let items = selector.selectAll('.cm-item').data(this.data());
		items.exit().remove();
		items.enter().append('div').call(this.drawItem).merge(items).call(this.setItem);
	}

	draw(options) {
		this.renderTemplate(options);
		d3.select(this.container).append('div')
			.classed(options.css, true).attr('id', options.containerID)
			.call(this.drawItems, options);
		return options;
	}

	redraw(options) {
		d3.select(this.container).select(`#${options.containerID}`).call(this.drawItems, options);
		return options;
	}

	getQueryParams() {
		let {itemKey, queryKey} = this.options;
		let onItems = Array.from(this.toggles.toggledOn(itemKey));
		if (!queryKey) {
			return onItems;
		}
		let re = {};
		if (onItems.length !== this.toggles.size) {
			re[queryKey] = onItems;
		}
		return re;
	}

	getInfoContent() {
		let items = [];
		for (let item of this.toggles.toggledOn(this.options.itemKey)) {
			items.push(`<span>${item}</span>`);
		}
		return items.join(', ');
	}

	reset() {
		this.toggles.allOn();
		this.update();
	}
}

function togglesAPIMixin(superclass) {
	return class extends APIController.mixin(superclass) {
		processResponse(response) {
			response = super.processResponse(response);
			this.toggles.update(response.data);
			return response;
		}
	}
}

class TogglesController extends BasicTogglesController {}

exports.APIMixin = togglesAPIMixin;
exports.TogglesController = TogglesController;
exports.TogglesAPIController = togglesAPIMixin(TogglesController);

class BarTogglesController extends BasicTogglesController {
	constructor(options) {
		super(Object.assign({
			getMeasure: _.property('value'),
			numeralFormat: '0,0a', css: 'cm-toggles cm-checkboxes cm-bars'
		}, ...arguments));
		_.bindAll(this, 'itemMeasureSet', 'updateScale');
	}

	updateScale() {
		const items = Array.from(this.toggles.items.values());
		const max = _(items).map('value').map(this.options.getMeasure).max();
		this.scale = d3.scaleLinear().rangeRound([1, 100]).domain([0, max]);
	}

	draw(options, callback) {
		this.updateScale();
		return super.draw(options);
	}

	redraw(options, callback) {
		this.updateScale();
		return super.redraw(options);
	}

	itemText(el, d, i) {
		let {getTitle, getMeasure, numeralFormat} = this.options;
		let text = getTitle(d.value) || d.key;
		let measure = numeral(getMeasure(d.value) || 0).format(numeralFormat);
		return text + ' (' + measure + ')';
	}

	itemMeasureSet(selector) {
		let scale = this.scale;
		let getMeasure = this.options.getMeasure;
		let m = selector.select('div');
		let itemColourIndex = this.itemColourIndex;

		selector.attr('class', function (d, i) {
			return utils.cssClasses('cm-measure', 'zl-bg-ci-' + itemColourIndex(d, i));
		});

		m.attr('class', function (d, i) { return 'zl-swatch-ci-' + itemColourIndex(d, i); })
			.attr('style', function (d) { return 'width: ' + _.ceil(scale(getMeasure(d.value))) + '%;'; });
	}

	setItem(selector) {
		selector.attr('class', this.itemClass);
		selector.select('.cm-title').text(this.itemText).on('click', this.itemOnClick);
		selector.select('.cm-measure').call(this.itemMeasureSet);
		return selector;
	}

	drawItem(selector) {
		selector.append('div').attr('class', 'cm-title');
		selector.append('div').attr('class', 'cm-measure').append('div');
		return selector;
	}
}

exports.BarTogglesController = BarTogglesController;
exports.BarTogglesAPIController = togglesAPIMixin(BasicTogglesController);

let popupShown = null;
function showPopup(selector) {
	if (popupShown) {
		popupShown.classed('collapse', true);
	}
	selector.classed('collapse', false);
	popupShown = selector;
}

function hidePopup() {
	if (popupShown) {
		popupShown.classed('collapse', true);
		popupShown = null;
	}
}
window.addEventListener('click', hidePopup);

class PopupToggles extends BasicTogglesController {
	constructor(options) {
		super({css: 'cm-toggles cm-checkboxes cm-popup'}, ... arguments);
		utils.bindHandlers(this, 'infoContentOnClick');
	}

	draw(options, callback) {
		this.renderTemplate(options);
		let c = d3.select(this.container).append('div').classed(options.css, true);
		c.append('a')
			.attr('href', '#').classed('cm-content-info', true)
			.html(this.getInfoContent()).on('click', this.infoContentOnClick);
		c.append('div')
			.classed('cm-items collapse', true)
			.attr('id', options.containerID)
			.on('click', function () {
				d3.event.stopPropagation();
			})
			.call(this.drawItems, options);
		return options;
	}

	redraw(options, callback) {
		let c = d3.select(this.container);
		c.select('.cm-content-info').html(this.getInfoContent());
		c.select(`#${options.containerID}`).call(this.drawItems, options);
		return options;
	}

	getInfoContent() {
		let items = Array.from(this.toggles.toggledOn(this.options.itemKey));
		items.sort();
		if (items.length <= 2) {
			return _(items).map(function (i) { return `<span>${i}</span>`; }).join(', ')
		}
		return `<span>${items[0]}</span>, <span>${items[1]}</span> and ${items.length - 2} more`;
	}

	itemOnClick(el, d, i) {
		super.itemOnClick(... arguments);
		d3.select(this.container)
			.select('.cm-content-info')
			.html(this.getInfoContent());
		return false;
	}

	infoContentOnClick() {
		d3.event.stopPropagation();
		d3.event.preventDefault();
		let items = d3.select(this.container).select('.cm-items');
		let collapsed = items.classed('collapse');
		if (collapsed) {
			showPopup(items);
		} else {
			hidePopup();
		}
	}
}
exports.PopupToggles = PopupToggles;
